import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { UserDevice } from '@/types/user-management';

interface UserDevicesTabProps {
  devices: UserDevice[];
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncateDeviceId(deviceId: string): string {
  if (deviceId.length <= 18) return deviceId;
  return `${deviceId.slice(0, 8)}…${deviceId.slice(-6)}`;
}

export function UserDevicesTab({ devices }: UserDevicesTabProps) {
  if (devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 space-y-2">
        <h3 className="text-base font-semibold">No devices recorded yet</h3>
        <p className="text-sm text-muted-foreground text-center">
          A device appears here after the user opens the mobile app and the client reports its
          installed build.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Platform</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Build</TableHead>
            <TableHead>Device ID</TableHead>
            <TableHead>First Seen</TableHead>
            <TableHead>Last Seen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map(device => (
            <TableRow key={`${device.deviceId}-${device.lastSeenAt}`}>
              <TableCell>
                <Badge variant={device.platform === 'ios' ? 'default' : 'secondary'}>
                  {device.platform.toUpperCase()}
                </Badge>
              </TableCell>
              <TableCell className="font-mono text-xs">{device.appVersion ?? '—'}</TableCell>
              <TableCell className="font-mono text-xs">{device.appBuild ?? '—'}</TableCell>
              <TableCell
                className="font-mono text-xs text-muted-foreground"
                title={device.deviceId}
              >
                {truncateDeviceId(device.deviceId)}
              </TableCell>
              <TableCell className="text-xs">{formatDateTime(device.firstSeenAt)}</TableCell>
              <TableCell className="text-xs">{formatDateTime(device.lastSeenAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
